import { useEffect } from 'react'
import type { GuestProperty } from '@/lib/types'

interface Props {
  property: GuestProperty
  guide_url: string
  content_state?: 'empty' | 'thin' | 'ready'
}

// Honest CTA copy: don't promise "explore the neighbourhood" over a thin/empty guide.
const CTA_LABEL: Record<string, string> = {
  empty: 'Get connected & house info',
  thin:  'See local picks',
  ready: 'Explore the neighbourhood',
}

function hostInitials(name: string | null): string {
  if (!name) return '?'
  return name
    .split(/\s+/)
    .map(w => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export default function CaptivePortal({ property, guide_url, content_state = 'ready' }: Props) {
  useEffect(() => {
    document.documentElement.style.setProperty('--brand', property.brand_color)
  }, [property.brand_color])

  return (
    <div
      className="relative flex min-h-dvh flex-col overflow-hidden"
      style={{ background: property.brand_color, fontFamily: "'Geist', system-ui, sans-serif" }}
    >
      {/* Cover photo — below all overlay layers; gradient/duotone paint on top */}
      {property.cover_image_url && (
        <img
          src={property.cover_image_url}
          alt=""
          aria-hidden="true"
          style={{
            position: 'absolute', inset: 0,
            width: '100%', height: '100%',
            objectFit: 'cover', objectPosition: 'center',
          }}
        />
      )}

      {/* Warm coastal gradient background — only shown when there is no cover photo,
          since it is opaque and would hide the photo completely. The duotone overlay
          and bottom fade below are enough to brand a real photo. */}
      {!property.cover_image_url && (
        <div
          className="absolute inset-0"
          style={{
            background: 'radial-gradient(120% 80% at 50% 110%, oklch(0.78 0.10 60) 0%, oklch(0.68 0.13 45) 25%, oklch(0.55 0.14 30) 55%, oklch(0.30 0.06 250) 100%)',
            zIndex: 0,
          }}
        />
      )}

      {/* Duotone color overlay */}
      <div
        className="absolute inset-0"
        style={{ background: property.brand_color, mixBlendMode: 'color', zIndex: 1 }}
      />

      {/* Bottom fade to brand */}
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(180deg, transparent 35%, ${property.brand_color}cc 100%)`,
          zIndex: 1,
        }}
      />

      {/* Fine grain texture */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)',
          backgroundSize: '3px 3px',
          zIndex: 2,
          mixBlendMode: 'overlay',
        }}
      />

      {/* Content */}
      <div className="relative z-10 flex flex-1 flex-col px-7 text-white" style={{ paddingTop: 64 }}>
        <div className="flex-1" />

        {/* Property identity */}
        <div className="mb-4">
          {property.tagline && (
            <p
              className="g-mono mb-3 text-xs uppercase tracking-wider opacity-75"
              style={{ letterSpacing: '0.18em' }}
            >
              {property.tagline}
            </p>
          )}
          <h1
            className="g-serif leading-none"
            style={{ fontSize: 52, letterSpacing: '-0.02em', marginBottom: 16 }}
          >
            {property.name}
          </h1>
          {property.tagline && (
            <p
              className="g-serif italic opacity-90"
              style={{ fontSize: 20, lineHeight: 1.3, maxWidth: 280 }}
            >
              {property.tagline}
            </p>
          )}
        </div>

        {/* CTA */}
        <a
          href={guide_url}
          className="g-tap mt-7 flex items-center justify-between rounded-full px-5"
          style={{
            background: '#FBF7F1',
            color: '#1F1A14',
            paddingTop: 20,
            paddingBottom: 20,
            marginBottom: 36,
            fontSize: 17,
            fontWeight: 500,
            letterSpacing: '-0.01em',
            textDecoration: 'none',
            boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
          }}
        >
          <span>{CTA_LABEL[content_state] ?? CTA_LABEL.ready}</span>
          <span
            className="flex items-center justify-center rounded-full"
            style={{ width: 36, height: 36, background: '#1F1A14', color: '#FBF7F1', flexShrink: 0 }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </span>
        </a>

        {/* Footer */}
        <div
          className="g-mono flex justify-between uppercase opacity-55"
          style={{
            paddingBottom: 'calc(32px + env(safe-area-inset-bottom))',
            fontSize: 10,
            letterSpacing: '0.22em',
          }}
        >
          {property.host_name && <span>A guide by {property.host_name}</span>}
          <span>INNLY</span>
        </div>
      </div>
    </div>
  )
}
